import React from 'react';

import StatusPill from '../shared/status_pill';
import { fmt_count } from '../format';
import { short_uuid } from '../overview/adapters';
import { summary_view } from './logic';

const Stat = ({ label, value, hint }) => (
  <div className="tp-card-field">
    <span className="tp-card-field-label">{label}</span>
    <span className="tp-strong">{value}</span>
    {hint ? <span className="tp-hint">{hint}</span> : null}
  </div>
);

// Name plus the short uuid (full on hover), as the Needs-attention lists show a teacher.
const TeacherName = ({ t }) => (
  <span>
    {t.name}
    {t.uuid ? (
      <span className="tp-card-field-label tp-mono" title={t.uuid}>
        {' '}
        {short_uuid(t.uuid)}
      </span>
    ) : null}
  </span>
);

const MonthList = ({ rows }) => (
  <ul style={{ margin: '6px 0 0 18px' }}>
    {rows.map((b) => (
      <li key={b.key}>
        <TeacherName t={b.teacher} />, {b.month}
        {b.error ? `: ${b.error}` : ''}
      </li>
    ))}
  </ul>
);

const PreviewSummary = ({ summary, names, catalogue, months_text }) => {
  const s = summary_view(summary, names, catalogue);
  return (
    <section className="tp-card" aria-labelledby="tp-rx-summary">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" id="tp-rx-summary">
          Impact
        </span>
        {months_text ? (
          <span className="tp-card-hd-sub">Open months checked: {months_text}</span>
        ) : null}
      </div>

      {s.blocking_count || s.lost_pay_decisions ? (
        <div className="tp-banner tp-banner-error" role="alert">
          {s.blocking_count
            ? `${fmt_count(s.blocking_count)} teacher-month(s) are priced today but cannot be priced under the new rules. `
            : ''}
          {s.lost_pay_decisions
            ? `${fmt_count(s.lost_pay_decisions)} class(es) would have no pay rule. `
            : ''}
          This change cannot be saved. Fix it and preview again.
          {s.blocking.length ? <MonthList rows={s.blocking} /> : null}
        </div>
      ) : null}

      <div className="tp-card-item-grid">
        <Stat label="Teachers whose pay changes" value={s.teachers_text} />
        <Stat label="Teacher-months checked" value={fmt_count(s.teacher_months)} />
        <Stat label="Total change in payable" value={s.total_delta_text} />
        <Stat
          label="Money moved (either way)"
          value={s.abs_delta_text}
          hint="The sum of every increase and decrease; used for the approval check."
        />
        <Stat
          label="Free Pass Days placed"
          value={s.free_pass_days > 0 ? `+${s.free_pass_days}` : String(s.free_pass_days)}
        />
        <Stat label="Teachers whose month goes negative" value={fmt_count(s.going_negative)} />
      </div>

      {s.going_negative_teachers.length ? (
        <div style={{ marginTop: 12 }}>
          <div className="tp-strong">Teachers going negative</div>
          <ul style={{ margin: '4px 0 0 18px' }}>
            {s.going_negative_teachers.map((t) => (
              <li key={t.key}>
                <TeacherName t={t} />
              </li>
            ))}
          </ul>
          <div className="tp-hint">The shortfall carries forward to the next month.</div>
        </div>
      ) : null}

      {s.types.length ? (
        <table className="tp-table" style={{ marginTop: 12 }}>
          <caption className="tp-hint" style={{ textAlign: 'left' }}>
            Pay, penalty and incentive lines that change
          </caption>
          <thead>
            <tr>
              <th scope="col">Line</th>
              <th scope="col">Added</th>
              <th scope="col">Removed</th>
              <th scope="col">Changed</th>
              <th scope="col">Money</th>
            </tr>
          </thead>
          <tbody>
            {s.types.map((t) => (
              <tr key={t.key}>
                <td>{t.label}</td>
                <td>{fmt_count(t.added)}</td>
                <td>{fmt_count(t.removed)}</td>
                <td>{fmt_count(t.changed)}</td>
                <td>{t.delta_text}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {s.flags_added.length || s.flags_removed.length ? (
        <div style={{ marginTop: 12 }}>
          <div className="tp-strong">Review flags</div>
          <ul style={{ margin: '4px 0 0 18px' }}>
            {s.flags_added.map((f) => (
              <li key={`a-${f.label}`}>
                {f.label}: {fmt_count(f.count)} more{' '}
                {f.blocks_close ? <StatusPill tone="orange">Blocks month close</StatusPill> : null}
              </li>
            ))}
            {s.flags_removed.map((f) => (
              <li key={`r-${f.label}`}>
                {f.label}: {fmt_count(f.count)} fewer
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {s.matches.length ? (
        <table className="tp-table" style={{ marginTop: 12 }}>
          <caption className="tp-hint" style={{ textAlign: 'left' }}>
            Classes each changed rule decided
          </caption>
          <thead>
            <tr>
              <th scope="col">Rule</th>
              <th scope="col">Before</th>
              <th scope="col">After</th>
            </tr>
          </thead>
          <tbody>
            {s.matches.map((m) => (
              <tr key={m.key}>
                <td>
                  {m.name}
                  {m.matches_nothing ? (
                    <div className="tp-err">Matched no class in the checked months.</div>
                  ) : null}
                </td>
                <td>{m.before}</td>
                <td>{m.after}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {s.samples.length ? (
        <div style={{ marginTop: 12 }}>
          <div className="tp-strong">What a teacher will read</div>
          {s.samples.map((x) => (
            <blockquote
              key={x.key}
              className="tp-banner tp-banner-info"
              style={{ margin: '6px 0' }}
            >
              <div className="tp-strong">{x.title}</div>
              <div>{x.text}</div>
              {x.teacher ? (
                <div className="tp-hint">
                  From a class of <TeacherName t={x.teacher} />
                </div>
              ) : null}
              {x.footnote ? <div className="tp-hint">{x.footnote}</div> : null}
              {!x.complete ? (
                <div className="tp-err">
                  Some placeholders were not filled; check the teacher wording.
                </div>
              ) : null}
            </blockquote>
          ))}
        </div>
      ) : null}

      {s.unpriced_count ? (
        <div className="tp-hint" style={{ marginTop: 12 }}>
          {fmt_count(s.unpriced_count)} teacher-month(s) could not be priced under either rule set.
          That is a data problem, not this change, and does not block saving.
          {s.unpriced.length ? <MonthList rows={s.unpriced} /> : null}
        </div>
      ) : null}
    </section>
  );
};

export default PreviewSummary;
